import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  input,
} from "@angular/core";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { Observable, skipWhile } from "rxjs";
import { map } from "rxjs/operators";
import { FormAttachmentGroupTypes } from "../../../locale/consts.en";
import { LibraryDocument } from "../../library/library.model";
import { DatabaseService } from "../../shared/database.service";
import { DescriptiveFormTemplate, FormTableType } from "../forms.model";
import { FormsService } from "../forms.service";
// import * as buffer from 'buffer';
import { sortByStringWithEmptyAtEnd } from "src/app/shared/helpers/helper-functions";
import { MasterDataService } from "../../master-data/master-data-service";
import { ProductCategory } from "../../master-data/master-data.model";

// (window as any).Buffer = buffer.Buffer;

@Component({
  selector: "app-form-edit",
  templateUrl: "./form-edit.component.html",
  styleUrls: ["./form-edit.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FormEditComponent implements OnInit {
  formsService = inject(FormsService);
  private databaseService = inject(DatabaseService);
  private masterDataService = inject(MasterDataService);

  readonly editForm = input<any>(undefined); //

  FTableType = FormTableType;

  attachmentGroupTypes = FormAttachmentGroupTypes;
  productCategories: ProductCategory[] = [];

  maxColumns = 4;
  loadedTemplates$: Observable<DescriptiveFormTemplate[]>;
  loadedDocuments$: Observable<LibraryDocument[]>;

  file: unknown;
  currentDrawingForm: FormControl = null;

  constructor() {
    const formsService = this.formsService;

    this.loadedTemplates$ = formsService.loadedForms$;
    this.loadedDocuments$ = this.databaseService
      .loadDocuments()
      .pipe(
        map((docs) => sortByStringWithEmptyAtEnd(docs, (item) => item.name)),
      );
    // .pipe(map((docs) => docs.sort((a, b) => a.name.localeCompare(b.name))));
  }

  ngOnInit() {
    this.masterDataService
      .getProductCategories()
      .pipe(skipWhile((pc) => pc == null || pc.length === 0))
      .subscribe((prodCateg) => {
        this.productCategories = prodCateg;
      });
    this.databaseService.loadConfig().subscribe((response) => {
      if (response) {
        const mp = new Map(Object.entries(response.configMap));
        this.maxColumns = mp.get("form_table_max_col")
          ? +mp.get("form_table_max_col")
          : 4;
      }
    });
  }

  public compareFn(c1: LibraryDocument, c2: LibraryDocument): boolean {
    return c1 && c2 && c1.uuid === c2.uuid;
  }

  addItem(section: number) {
    this.formsService.addItem(section);
  }

  deleteItem(index: number, section: number) {
    this.formsService.deleteItem(index, section);
  }

  addSection() {
    this.formsService.addSection();
  }

  deleteSection(index: number) {
    this.formsService.deleteSection(index);
  }

  addField() {
    this.formsService.addField();
  }

  deleteField(field: number) {
    this.formsService.deleteField(field);
  }

  addTableCol(table: number, type: unknown) {
    this.formsService.addTableColumn(table, type as FormTableType);
  }

  deleteTableCol(index: number, table: number) {
    this.formsService.deleteTableColumn(index, table);
  }

  addTable(type: FormTableType) {
    this.formsService.addTable(type);
  }

  getFormArrayValidity(i): boolean {
    return (
      (this.editForm().controls.tables as FormArray).controls[i] as FormGroup
    )?.invalid;
  }
  deleteTable(table: number) {
    this.formsService.deleteTable(table);
  }

  addSignature() {
    this.formsService.addSignature();
  }

  deleteSignature(index: number) {
    this.formsService.deleteSignature(index);
  }

  addDrawing() {
    this.formsService.addDrawing();
  }

  deleteDrawing(index: number) {
    this.formsService.deleteDrawing(index);
  }

  addAttachment() {
    this.formsService.addAttachment();
  }

  deleteAttachment(index: number) {
    this.formsService.deleteAttachment(index);
  }

  addAttachmentGroup() {
    this.formsService.addAttachmentGroup();
  }

  deleteAttachmentGroup(index: number) {
    this.formsService.deleteAttachmentGroup(index);
  }

  addAttachmentToGroup(grIndex: number) {
    this.formsService.addAttachmentToGroup(grIndex);
  }

  deleteAttachmentFromGroup(docIndex: number, attIndex: number) {
    this.formsService.deleteAttachmentFromGroup(docIndex, attIndex);
  }

  addInteractiveAttachmentGroup() {
    this.formsService.addInteractiveAttachmentGroup();
  }

  deleteInteractiveAttachmentGroup(attIndex: number) {
    this.formsService.deleteInteractiveAttachmentGroup(attIndex);
  }

  addInteractiveAttachmentToGroup(grIndex: number) {
    this.formsService.addInteractiveAttachmentToGroup(grIndex);
  }

  deleteInteractiveAttachmentFromGroup(attGrIndex: number) {
    this.formsService.deleteInteractiveAttachmentFromGroup(attGrIndex);
  }

  addDeviceAttachmentGroup() {
    this.formsService.addDeviceAttachmentGroup();
  }

  deleteDeviceAttachmentGroup(index: number) {
    this.formsService.deleteDeviceAttachmentGroup(index);
  }

  addDeviceAttachmentToGroup(grIndex: number) {
    this.formsService.addDeviceAttachmentToGroup(grIndex);
  }

  deleteDeviceAttachmentFromGroup(docIndex: number, attIndex: number) {
    this.formsService.deleteDeviceAttachmentFromGroup(docIndex, attIndex);
  }

  addDocumentLink() {
    this.formsService.addDocumentLink();
  }

  deleteDocumentLink(index: number) {
    this.formsService.deleteDocumentLink(index);
  }

  getGroups(form: FormGroup) {
    return (form.get("groups") as FormArray).controls as FormGroup[];
  }

  getFields(form: FormGroup) {
    return (form.get("fields") as FormArray).controls;
  }

  getTables(form: FormGroup) {
    return (form.get("tables") as FormArray).controls as FormGroup[];
  }

  getSignatures(form: FormGroup) {
    return (form.get("signatures") as FormArray).controls as FormGroup[];
  }

  getDrawings(form: FormGroup) {
    return (form.get("drawings") as FormArray).controls as FormGroup[];
  }

  getAttachments(form: FormGroup) {
    return (form.get("attachments") as FormArray).controls as FormGroup[];
  }

  getAttachmentGroups(form: FormGroup) {
    return (form.get("attachmentGroups") as FormArray).controls as FormGroup[];
  }

  getInteractiveAttachmentGroups(form: FormGroup) {
    return (form.get("interactiveAttachmentGroups") as FormArray)
      .controls as FormGroup[];
  }

  getDeviceAttachmentGroups(form: FormGroup) {
    return (form.get("deviceAttachmentGroups") as FormArray)
      .controls as FormGroup[];
  }
  getDocuments(form: FormGroup) {
    return (form.get("documents") as FormArray).controls as FormGroup[];
  }

  fileChange(e: Event, form: FormControl) {
    this.file = (e.target as HTMLInputElement).files[0];
    this.uploadDocument(this.file);
    this.currentDrawingForm = form;
  }

  uploadDocument(file) {
    const fileReader = new FileReader();
    fileReader.onload = this.handleReaderLoaded.bind(this);
    fileReader.readAsBinaryString(file);
  }

  handleReaderLoaded(readerEvt) {
    const binaryString = readerEvt.target.result;
    // this.currentDrawingForm.setValue(btoa(binaryString));  //deprecated
    this.currentDrawingForm.setValue(
      Buffer.from(binaryString, "binary").toString("base64"),
    );
  }

  openInput(index) {
    document.getElementById(index).click();
  }

  changeBackground(
    $event: MatCheckboxChange,
    base64PngBackground: FormControl,
  ) {
    if ($event.checked) {
      base64PngBackground.setValidators([]);
      base64PngBackground.updateValueAndValidity();
    } else {
      base64PngBackground.setValidators([Validators.required]);
      base64PngBackground.updateValueAndValidity();
    }
  }
  //protected readonly FormArray = FormArray;

  getFormGroup(item: unknown) {
    return item as FormGroup;
  }

  getFormControl(item: unknown) {
    return item as FormControl;
  }

  getFormArray(item: unknown) {
    return item as FormArray;
  }

  protected readonly FormControl = FormControl;
}
